import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Plus, Search, FileText,
    Download, Edit, Eye, Trash2,
    Calendar, DollarSign, Grid, List, BookTemplate,
    MoreHorizontal
} from 'lucide-react';
import { useContractsStore } from '../../store/contractsStore';
import { Contract } from '../../services/contracts.service';
import AddContractModal from './AddContractModal';
import ViewContractModal from './ViewContractModal';

const ContractsPage: React.FC = () => {
    const location = useLocation();
    const {
        contracts,
        isLoading,
        fetchContracts,
        deleteContract,
        fetchStats,
        fetchTemplates,
        templates,
        getFilteredContracts,
        getActiveContracts,
        getPendingSignatureContracts,
        searchTerm,
        setSearchTerm,
        filterStatus,
        setFilterStatus,
        filterTab,
        setFilterTab
    } = useContractsStore();

    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
    const [editingContract, setEditingContract] = useState<Contract | null>(null);
    const [preselectedPartnership, setPreselectedPartnership] = useState<any>(null);

    // Derive selected contract from store to ensure reactivity
    const selectedContract = selectedContractId
        ? contracts.find(c => c.id === selectedContractId) || null
        : null;

    useEffect(() => {
        fetchContracts();
        fetchStats();
        fetchTemplates();
    }, [fetchContracts, fetchStats, fetchTemplates]);

    // Handle navigation state
    useEffect(() => {
        if (location.state?.viewContractId) {
            setSelectedContractId(location.state.viewContractId);
            setIsViewModalOpen(true);
            // Clear state to prevent reopening on refresh
            window.history.replaceState({}, document.title);
        }
        if (location.state?.createForPartnership) {
            setPreselectedPartnership(location.state.createForPartnership);
            setIsAddModalOpen(true);
            // Clear state
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const filteredContracts = getFilteredContracts();
    const activeContracts = getActiveContracts();
    const pendingContracts = getPendingSignatureContracts();

    // Calculate totals
    const totalValue = filteredContracts.reduce((sum, c) => sum + c.totalValue, 0);
    const activeValue = activeContracts.reduce((sum, c) => sum + c.totalValue, 0);
    const pendingValue = pendingContracts.reduce((sum, c) => sum + c.totalValue, 0);

    // Format currency
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    // Format date
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    // Get status badge styles
    const getStatusBadge = (status: Contract['status']) => {
        const styles: Record<string, string> = {
            draft: 'from-gray-400 to-gray-500 text-white',
            sent: 'from-blue-400 to-blue-600 text-white',
            negotiation: 'from-yellow-400 to-orange-500 text-white',
            signed: 'from-purple-400 to-purple-600 text-white',
            active: 'from-green-400 to-green-600 text-white',
            completed: 'from-emerald-400 to-emerald-600 text-white',
            terminated: 'from-red-400 to-red-600 text-white',
        };
        return styles[status] || styles.draft;
    };

    // Calculate deliverables progress
    const getDeliverablesProgress = (contract: Contract) => {
        if (!contract.deliverables || contract.deliverables.length === 0) return 0;
        const completed = contract.deliverables.filter(
            d => d.status === 'completed' || d.status === 'approved'
        ).length;
        return Math.round((completed / contract.deliverables.length) * 100);
    };

    // Handle view contract
    const handleViewContract = (contract: Contract) => {
        setSelectedContractId(contract.id);
        setIsViewModalOpen(true);
    };

    // Handle edit contract
    const handleEditContract = (contract: Contract) => {
        setEditingContract(contract);
        setIsAddModalOpen(true);
    };

    // Handle delete contract
    const handleDeleteContract = async (contract: Contract) => {
        if (window.confirm(`Are you sure you want to delete "${contract.title}"?`)) {
            await deleteContract(contract.id);
        }
    };

    // Handle modal close
    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
        setEditingContract(null);
    };

    // Handle download contract (generates printable view)
    const handleDownloadContract = (contract: Contract) => {
        // Create a printable window with contract details
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        const deliverablesList = contract.deliverables?.map(d =>
            `<li><strong>${d.title}</strong>: ${d.description || 'No description'}</li>`
        ).join('') || '<li>No deliverables</li>';

        const milestonesList = contract.paymentTerms?.milestones?.map(m =>
            `<li>${m.name}: ${m.percentage}% ($${(contract.totalValue * m.percentage / 100).toLocaleString()})</li>`
        ).join('') || '';

        // Get creator and brand names
        const creatorName = 'Content Creator'; // Creator name from user's profile would be here
        const brandName = contract.partnership?.brand || 'Brand Partner';

        // Build signature content - check if signature is base64 image or text
        const renderSignature = (signature: string | undefined) => {
            if (!signature) return '';
            if (signature.startsWith('data:image')) {
                return `<img src="${signature}" alt="Signature" style="max-height: 50px; margin: 10px 0;" />`;
            }
            return `<p style="font-family: 'Dancing Script', cursive; font-size: 24px; margin: 10px 0;">${signature}</p>`;
        };

        const creatorSignatureContent = contract.signatures?.creatorSignedAt
            ? `
                ${renderSignature(contract.signatures.creatorSignature)}
                <p><strong>${creatorName}</strong></p>
                <p style="color: #16a34a;">✓ Signed on ${new Date(contract.signatures.creatorSignedAt).toLocaleDateString()}</p>
            `
            : '<p style="color: #6b7280;">(Pending Signature)</p>';

        const brandSignatureContent = contract.signatures?.brandSignedAt
            ? `
                ${renderSignature(contract.signatures.brandSignature)}
                <p><strong>${brandName}</strong></p>
                <p style="color: #16a34a;">✓ Signed on ${new Date(contract.signatures.brandSignedAt).toLocaleDateString()}</p>
            `
            : '<p style="color: #6b7280;">(Pending Signature)</p>';

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>${contract.title} - Contract Agreement</title>
                <link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap" rel="stylesheet">
                <style>
                    body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                    h1 { text-align: center; color: #1e293b; }
                    h2 { color: #334155; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px; }
                    .header { text-align: center; margin-bottom: 40px; }
                    .parties { background: #f8fafc; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
                    .section { margin-bottom: 30px; }
                    .signature-block { display: flex; justify-content: space-between; margin-top: 60px; }
                    .signature { width: 45%; text-align: center; padding: 20px; border: 1px solid #e2e8f0; border-radius: 8px; }
                    .signature-label { font-weight: bold; color: #334155; margin-bottom: 15px; font-size: 14px; }
                    @media print { body { padding: 20px; } }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>CONTRACT AGREEMENT</h1>
                    <p><strong>${contract.title}</strong></p>
                    <p>Contract Date: ${new Date(contract.startDate).toLocaleDateString()}</p>
                </div>

                <div class="parties">
                    <p><strong>PARTY A (Creator):</strong> ${creatorName}</p>
                    <p><strong>PARTY B (Brand):</strong> ${brandName}</p>
                </div>

                <div class="section">
                    <h2>1. Scope of Work</h2>
                    <p>The Creator agrees to produce the following deliverables:</p>
                    <ul>${deliverablesList}</ul>
                </div>

                <div class="section">
                    <h2>2. Compensation</h2>
                    <p><strong>Total Value:</strong> $${contract.totalValue.toLocaleString()}</p>
                    <p><strong>Payment Terms:</strong> ${contract.paymentTerms?.type || 'As agreed'}</p>
                    ${milestonesList ? `<ul>${milestonesList}</ul>` : ''}
                </div>

                <div class="section">
                    <h2>3. Term</h2>
                    <p>This agreement shall commence on ${new Date(contract.startDate).toLocaleDateString()} and continue until ${new Date(contract.endDate).toLocaleDateString()}.</p>
                </div>

                <div class="section">
                    <h2>4. Rights & Usage</h2>
                    <p>Usage Rights: ${contract.compliance?.usageRightsYears || 'N/A'} Year(s)</p>
                    <p>Exclusivity: ${contract.compliance?.exclusivityDays || 'N/A'} Days</p>
                    <p>FTC Disclosure Required: ${contract.compliance?.ftcDisclosure ? 'Yes' : 'No'}</p>
                </div>

                ${contract.notes ? `<div class="section"><h2>5. Additional Notes</h2><p>${contract.notes}</p></div>` : ''}

                <div class="signature-block">
                    <div class="signature">
                        <div class="signature-label">CREATOR SIGNATURE</div>
                        ${creatorSignatureContent}
                    </div>
                    <div class="signature">
                        <div class="signature-label">BRAND SIGNATURE</div>
                        ${brandSignatureContent}
                    </div>
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const statuses = ['all', 'draft', 'sent', 'negotiation', 'signed', 'active', 'completed', 'terminated'];

    if (isLoading) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Enhanced Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Contracts & Legal</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your contracts, agreements, and legal documents</p>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-3 gap-4 w-full max-w-lg">
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Total Value</div>
                        <div className="text-xl font-bold text-gray-900 dark:text-white text-center break-all">
                            {formatCurrency(totalValue)}
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Active</div>
                        <div className="text-xl font-bold text-green-600 text-center break-all">
                            {formatCurrency(activeValue)}
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Pending</div>
                        <div className="text-xl font-bold text-orange-600 text-center break-all">
                            {formatCurrency(pendingValue)}
                        </div>
                    </div>
                </div>
            </div>

            {/* Enhanced Filters & Actions */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex flex-col sm:flex-row gap-4 flex-1">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                            <input
                                type="text"
                                placeholder="Search contracts, brands, or tags..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
                            />
                        </div>
                        <div className="flex gap-2">
                            <select
                                value={filterStatus}
                                onChange={(e) => {
                                    setFilterStatus(e.target.value);
                                    // Reset filterTab to 'all' when using status filter
                                    if (e.target.value !== 'all') {
                                        setFilterTab('all');
                                    }
                                }}
                                className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 transition-colors capitalize"
                            >
                                {statuses.map(status => (
                                    <option key={status} value={status} className="capitalize">{status === 'all' ? 'All Status' : status}</option>
                                ))}
                            </select>
                            {(filterTab !== 'all' || filterStatus !== 'all') && (
                                <button
                                    onClick={() => {
                                        setFilterTab('all');
                                        setFilterStatus('all');
                                    }}
                                    className="flex items-center gap-2 px-4 py-2.5 border border-red-200 dark:border-red-600 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 transition-colors"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'grid' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                    }`}
                            >
                                <Grid className="w-4 h-4" />
                                Grid
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                    }`}
                            >
                                <List className="w-4 h-4" />
                                List
                            </button>
                        </div>
                        <button
                            onClick={() => {
                                setEditingContract(null);
                                setIsAddModalOpen(true);
                            }}
                            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                        >
                            <Plus className="w-4 h-4" />
                            New Contract
                        </button>
                    </div>
                </div>
            </div>


            {/* Templates View */}
            {filterTab === 'templates' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {templates.length === 0 ? (
                        <div className="col-span-full bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/30 dark:to-purple-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                                <BookTemplate className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No templates yet</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                                Create reusable contract templates for faster workflow
                            </p>
                            <button className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl">
                                Create Template
                            </button>
                        </div>
                    ) : (
                        templates.map((template) => (
                            <div
                                key={template.id}
                                className="group bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200 overflow-hidden"
                            >
                                <div className="p-6">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                                            <BookTemplate className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <span className="px-2.5 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg capitalize">
                                            {template.category}
                                        </span>
                                    </div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                        {template.name}
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-4">
                                        {template.description || 'No description'}
                                    </p>
                                </div>
                                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-100 dark:border-gray-600">
                                    <button className="w-full py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-xl hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors">
                                        Use Template
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            ) : (
                /* Contracts Display */
                <>
                    {filteredContracts.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/30 dark:to-purple-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                                <FileText className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No contracts found</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                                {searchTerm
                                    ? "Try adjusting your search terms"
                                    : "You don't have any contracts yet. Create your first one to get started."}
                            </p>
                            {!searchTerm && (
                                <button
                                    onClick={() => setIsAddModalOpen(true)}
                                    className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                                >
                                    Create Contract
                                </button>
                            )}
                        </div>
                    ) : viewMode === 'grid' ? (
                        /* Grid View */
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredContracts.map((contract) => (
                                <div
                                    key={contract.id}
                                    className="group bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200 overflow-hidden flex flex-col h-full"
                                >
                                    <div className="p-6 flex-1">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-1">
                                                    {contract.title}
                                                </h3>
                                                {contract.partnership?.brand && (
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">{contract.partnership.brand}</p>
                                                )}
                                            </div>
                                            <button className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                                <MoreHorizontal className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                            </button>
                                        </div>

                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getStatusBadge(contract.status)} capitalize`}>
                                                    {contract.status}
                                                </span>
                                                <div className="text-right">
                                                    <div className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(contract.totalValue)}</div>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatDate(contract.endDate)}
                                                </span>
                                            </div>

                                            {contract.deliverables && contract.deliverables.length > 0 && (
                                                <div>
                                                    <div className="flex items-center justify-between text-xs mb-1">
                                                        <span className="text-gray-500 dark:text-gray-400">Progress</span>
                                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                                            {getDeliverablesProgress(contract)}%
                                                        </span>
                                                    </div>
                                                    <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-primary-500 rounded-full transition-all"
                                                            style={{ width: `${getDeliverablesProgress(contract)}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {contract.tags && contract.tags.length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    {contract.tags.slice(0, 2).map(tag => (
                                                        <span key={tag} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md text-xs">
                                                            {tag}
                                                        </span>
                                                    ))}
                                                    {contract.tags.length > 2 && (
                                                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md text-xs">
                                                            +{contract.tags.length - 2}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-100 dark:border-gray-600">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleEditContract(contract)}
                                                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleViewContract(contract)}
                                                    className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDownloadContract(contract)}
                                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
                                                    title="Download Contract"
                                                >
                                                    <Download className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteContract(contract)}
                                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        /* List View */
                        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contract</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Value</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Duration</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Progress</th>
                                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {filteredContracts.map(contract => (
                                            <tr key={contract.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center">
                                                        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm mr-3">
                                                            {contract.title.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">{contract.title}</p>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400">{contract.partnership?.brand || 'No brand'}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getStatusBadge(contract.status)} capitalize`}>
                                                        {contract.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center">
                                                        <DollarSign className="w-4 h-4 text-green-500 mr-1" />
                                                        <span className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(contract.totalValue)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                                        <p>{formatDate(contract.startDate)}</p>
                                                        <p className="text-gray-400">to {formatDate(contract.endDate)}</p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {contract.deliverables && contract.deliverables.length > 0 ? (
                                                        <div className="w-24">
                                                            <div className="flex items-center justify-between text-xs mb-1">
                                                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                                                    {getDeliverablesProgress(contract)}%
                                                                </span>
                                                            </div>
                                                            <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                                                <div
                                                                    className="h-full bg-primary-500 rounded-full"
                                                                    style={{ width: `${getDeliverablesProgress(contract)}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-sm">-</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleEditContract(contract)}
                                                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleViewContract(contract)}
                                                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDownloadContract(contract)}
                                                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all"
                                                            title="Download Contract"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteContract(contract)}
                                                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Add/Edit Contract Modal */}
            <AddContractModal
                isOpen={isAddModalOpen}
                onClose={() => {
                    handleCloseAddModal();
                    setPreselectedPartnership(null);
                }}
                editingContract={editingContract}
                preselectedPartnership={preselectedPartnership}
            />

            {/* View Contract Modal */}
            {selectedContract && (
                <ViewContractModal
                    isOpen={isViewModalOpen}
                    onClose={() => {
                        setIsViewModalOpen(false);
                        setSelectedContractId(null);
                    }}
                    contract={selectedContract}
                />
            )}
        </div>
    );
};

export default ContractsPage;
